import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RegisterFormData } from '../../types';
import { EducationLevelSelect } from '../form-fields';

interface AdditionalInfoProps {
  form: UseFormReturn<RegisterFormData>;
}

export function AdditionalInfo({ form }: AdditionalInfoProps) {
  const { register, formState: { errors }, watch, setValue } = form;
  const educationLevel = watch('educationLevel');
  const otherEducationLevel = watch('otherEducationLevel');

  const handleEducationLevelChange = (value: string) => {
    setValue('educationLevel', value, { shouldValidate: true });
    if (value !== 'other') {
      setValue('otherEducationLevel', '');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="region">المنطقة *</Label>
          <Input
            id="region"
            {...register('region', { required: 'هذا الحقل مطلوب' })}
            placeholder="أدخل المنطقة"
          />
          {errors.region && (
            <p className="text-sm text-red-500">{errors.region.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="occupation">المهنة *</Label>
          <Input
            id="occupation"
            {...register('occupation', { required: 'هذا الحقل مطلوب' })}
            placeholder="المهنة"
          />
          {errors.occupation && (
            <p className="text-sm text-red-500">{errors.occupation.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="branch">الفرع *</Label>
          <Input
            id="branch"
            {...register('branch', { required: 'هذا الحقل مطلوب' })}
            placeholder="مثل: حمود / راشد"
          />
          {errors.branch && (
            <p className="text-sm text-red-500">{errors.branch.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>المرحلة الدراسية *</Label>
          <EducationLevelSelect
            value={educationLevel}
            onChange={handleEducationLevelChange}
            error={errors.educationLevel?.message || (educationLevel === 'other' && errors.otherEducationLevel?.message)}
            otherValue={otherEducationLevel}
            onOtherChange={(value) => setValue('otherEducationLevel', value, { shouldValidate: true })}
          />
        </div>
      </div>

      {/* Account Credentials Section */}
      <div className="space-y-6 pt-6 border-t">
        <h3 className="text-lg font-semibold">بيانات تسجيل الدخول</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">رقم الجوال *</Label>
            <Input
              id="phoneNumber"
              type="tel"
              dir="ltr"
              className="text-left"
              {...register('phoneNumber', {
                required: 'رقم الجوال مطلوب',
                validate: (value) => {
                  if (!/^05\d{8}$/.test(value)) {
                    return 'رقم الجوال غير صحيح';
                  }
                  return true;
                }
              })}
              placeholder="05xxxxxxxx"
            />
            {errors.phoneNumber && (
              <p className="text-sm text-red-500">{errors.phoneNumber.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">كلمة المرور *</Label>
            <Input
              id="password"
              type="password"
              {...register('password', {
                required: 'كلمة المرور مطلوبة',
                minLength: {
                  value: 6,
                  message: 'يجب أن تحتوي كلمة المرور على 6 أرقام'
                },
                validate: (value) => {
                  if (!/\d{6}$/.test(value)) {
                    return 'يجب أن تحتوي كلمة المرور على أرقام فقط';
                  }
                  return true;
                }
              })}
            />
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">تأكيد كلمة المرور *</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword', {
                required: 'تأكيد كلمة المرور مطلوب',
                validate: (value) => 
                  value === watch('password') || 'كلمة المرور غير متطابقة'
              })}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}